// Redux
import api from '../redux-api';
import { Action, ApiAction, AsyncResult, Result } from '../index';
import { startLoaderAction, stopLoaderAction } from './ui/loaders';
import { hideModal } from '@/redux/modules/omni-risk-profile-modal';

// Enums
import { OmniRiskProfileEmailTemplate, OmniRiskProfileStatus } from '@/enums/OmniRiskProfiles';

// Types
import GenericContact from '@/types/generic-contact';
import OmniRiskProfilesQuestionnaire from '@/types/risk-profiles/omni/omni-risk-profiles-questionnaire';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { OmniRiskProfilesState } from './types/omni-risk-profiles';
import { SortingField } from '@/types/data';
import {
  AuthorizeEmailParams,
  BaseOmniRiskProfileParams,
  RefreshOmniRiskProfilesParams,
  UpdateOmniRiskProfileBody,
  UpdateOmniRiskProfileParams,
} from '@/types/risk-profiles/omni/omni-risk-profile-context-types';
import { UUID } from '@/types/global';
import Dictionary from '@/types/dictionary';

// Utils
import { generateLocalizedStringFromText } from '@/utils/i18n';
import { PERSISTENCE_KEYS, userHasOmniRiskProfilePersistenceKeyEnabled } from '@/utils/persistence';
import { replaceById } from '@/utils/objects';
import { sortByDateKey } from '@/utils/dates';
import localStorageUtils from '@/utils/local-storage';
import { Email } from '@/types/models/risk-profiles/omni-risk-profile-stats';
import { transformQuestionnaire } from '@/utils/omni-risk-profile';

// Initial State
const INITIAL_STATE: OmniRiskProfilesState = {
  currentRiskProfile: null,
  loading: false,
  questionnaire: undefined,
  riskProfiles: [],
};

const PERSISTENCE_KEYS_MAP: Dictionary<PERSISTENCE_KEYS> = {
  [PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT]: PERSISTENCE_KEYS.EXISTING_CLIENT_OMNI_RISK_PROFILE,
};

// Adds the omni persistence key to a params object if needed
// Omni risk profile persistence key is not stored in local storage directly, but derived from the contact persistence source
const applyPersistenceKey = <T extends Record<string, any>>(params: T, user: any): T & { persistenceKey?: string } => {
  const isEnabled = user && userHasOmniRiskProfilePersistenceKeyEnabled(user);

  if (!isEnabled) {
    return params;
  }

  const contactPersistenceKey = localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE);
  const omniRiskProfilePersistenceKey = contactPersistenceKey && PERSISTENCE_KEYS_MAP[contactPersistenceKey];

  return omniRiskProfilePersistenceKey ? { ...params, persistenceKey: omniRiskProfilePersistenceKey } : params;
};

// Actions
export const COMPLETE_QUESTIONNAIRE: Action = {
  type: 'connect/omni-risk-profiles/complete-questionnaire',
};

export const CREATE_OMNI_RISK_PROFILE: ApiAction = {
  type: 'connect/omni-risk-profiles/create-omni-risk-profile',
  endpoint: 'contacts/:contactId/omni-risk-profiles',
};

export const DELETE_OMNI_RISK_PROFILE: ApiAction = {
  type: 'connect/omni-risk-profiles/delete-risk-profile',
  endpoint: 'contacts/:contactId/omni-risk-profiles/:omniRiskProfileId/',
};

const GET_OMNI_RISK_PROFILES: ApiAction = {
  type: 'connect/omni-risk-profiles/get-risk-profiles',
  endpoint: 'contacts/:contactId/omni-risk-profiles',
};

export const LOAD_OMNI_RISK_PROFILE: Action = {
  type: 'connect/omni-risk-profiles/load-omni-risk-profile',
};

export const LOAD_OMNI_RISK_PROFILES_QUESTIONNAIRE: ApiAction = {
  type: 'connect/omni-risk-profiles/questionnaire/get-omni-questionnaire',
  endpoint: 'omni-risk-profiles/questionnaire',
};

export const REFRESH_OMNI_RISK_PROFILE: ApiAction = {
  type: 'connect/omni-risk-profiles/refresh-omni-risk-profile',
  endpoint: 'contacts/:contactId/omni-risk-profiles/:omniRiskProfileId/refresh-questionnaire',
};

export const REMOVE_EMAIL: ApiAction = {
  type: 'connect/omni-risk-profiles/remove-email',
  endpoint: 'contacts/:contactId/omni-risk-profiles/:omniRiskProfileId/remove-questionnaire-access',
};

export const AUTHORIZE_EMAIL: ApiAction = {
  type: 'connect/omni-risk-profiles/authorize-email',
  endpoint: 'contacts/:contactId/omni-risk-profiles/:omniRiskProfileId/send-email',
};

const UPDATE_OMNI_RISK_PROFILE: ApiAction = {
  type: 'connect/omni-risk-profiles/updateRiskProfile',
  endpoint: 'contacts/:contactId/omni-risk-profiles/:omniRiskProfileId/',
};

// Reducer
export default function reducer(state: OmniRiskProfilesState = INITIAL_STATE, action: Action): OmniRiskProfilesState {
  switch (action.type) {
    case `${CREATE_OMNI_RISK_PROFILE.type}_SUCCESS`: {
      const newRiskProfile = action.data.data as FormattedFullOmniRiskProfile;
      const riskProfiles = state.riskProfiles?.slice();

      if (riskProfiles) {
        riskProfiles.push(newRiskProfile);
        riskProfiles.sort(sortByDateKey);
      }

      return {
        ...state,
        riskProfiles,
      };
    }
    case `${DELETE_OMNI_RISK_PROFILE.type}_SUCCESS`: {
      const riskProfiles = state.riskProfiles?.slice().sort(sortByDateKey);

      return {
        ...state,
        riskProfiles: riskProfiles?.filter(riskProfile => riskProfile._id !== action.params.omniRiskProfileId),
      };
    }
    case GET_OMNI_RISK_PROFILES.type: {
      return {
        ...state,
        loading: true,
      };
    }
    case `${GET_OMNI_RISK_PROFILES.type}_ERROR`: {
      return {
        ...state,
        currentRiskProfile: null,
        loading: false,
        riskProfiles: [],
      };
    }
    case `${GET_OMNI_RISK_PROFILES.type}_SUCCESS`: {
      const loadedRiskProfiles = action.data.data as FormattedFullOmniRiskProfile[];
      const riskProfiles = loadedRiskProfiles?.slice().sort(sortByDateKey);

      return {
        ...state,
        loading: false,
        riskProfiles,
      };
    }
    case LOAD_OMNI_RISK_PROFILE.type: {
      return {
        ...state,
        currentRiskProfile: action.omniRiskProfile,
      };
    }
    case `${LOAD_OMNI_RISK_PROFILES_QUESTIONNAIRE.type}_ERROR`: {
      return {
        ...state,
        questionnaire: INITIAL_STATE.questionnaire,
      };
    }
    case `${LOAD_OMNI_RISK_PROFILES_QUESTIONNAIRE.type}_SUCCESS`: {
      return {
        ...state,
        questionnaire: transformQuestionnaire(action.data.data),
      };
    }
    case `${REFRESH_OMNI_RISK_PROFILE.type}_SUCCESS`: {
      const riskProfiles = state.riskProfiles?.slice();

      if (riskProfiles) {
        riskProfiles.sort(sortByDateKey);
      }

      return {
        ...state,
        riskProfiles,
      };
    }
    case `${REMOVE_EMAIL.type}_SUCCESS`:
      const updatedRiskProfileId = action.data.data._id;
      const removedEmails = action.data.data.removedEmails as string[];
      const newList =
        state.riskProfiles?.map(riskProfile => {
          if (riskProfile._id === updatedRiskProfileId) {
            return {
              ...riskProfile,
              emails: riskProfile.emails?.filter(email => !removedEmails.includes(email.email)),
            };
          }
          return riskProfile;
        }) || [];
      const riskProfiles = newList.sort(sortByDateKey);
      return {
        ...state,
        riskProfiles,
      };
    case `${AUTHORIZE_EMAIL.type}_SUCCESS`: {
      const updatedRiskProfileId = action.data.data._id;
      const emails = (action.data.data.emails ?? []) as Email[];
      const newList =
        state.riskProfiles?.map(riskProfile => {
          if (riskProfile._id === updatedRiskProfileId) {
            return {
              ...riskProfile,
              emails,
              status: action.data.data.status,
            };
          }
          return riskProfile;
        }) || [];
      const riskProfiles = newList.sort(sortByDateKey);
      return {
        ...state,
        riskProfiles,
      };
    }
    case `${UPDATE_OMNI_RISK_PROFILE.type}_SUCCESS`: {
      const updatedRiskProfile = action.data.data as FormattedFullOmniRiskProfile;
      const newList = replaceById(state.riskProfiles?.slice() || [], updatedRiskProfile, '_id') as FormattedFullOmniRiskProfile[];
      const riskProfiles = newList.sort(sortByDateKey);

      return {
        ...state,
        currentRiskProfile: null,
        riskProfiles,
      };
    }
    default:
      return state;
  }
}

// Action Creators
export const createOmniRiskProfile =
  (riskProfileName: string): AsyncResult<void> =>
  (dispatch, getState) => {
    const { contacts } = getState();

    if (!contacts.current) {
      return Promise.reject();
    }

    const contactId = contacts.current.contact._id;
    const name = generateLocalizedStringFromText(riskProfileName);

    return api.post(CREATE_OMNI_RISK_PROFILE, { contactId }, { name }).then(({ data }: { data: FormattedFullOmniRiskProfile }) => {
      dispatch(loadOmniRiskProfileById(data._id));
    });
  };

export const deleteOmniRiskProfile =
  (riskProfile: FormattedFullOmniRiskProfile): AsyncResult<void> =>
  dispatch => {
    if (!riskProfile) {
      return Promise.reject();
    }

    return api.delete(DELETE_OMNI_RISK_PROFILE, { contactId: riskProfile.contactId, omniRiskProfileId: riskProfile._id }).finally(() => {
      dispatch(hideModal());
    });
  };

export const getOmniRiskProfiles =
  (filterByContactId?: string, query?: string, nextToken?: string, sort?: SortingField[]): AsyncResult<void> =>
  (dispatch, getState) => {
    if (nextToken == null) {
      dispatch(startLoaderAction(GET_OMNI_RISK_PROFILES.type));
    }

    const {
      contacts,
      auth: { user },
    } = getState();

    let contact: GenericContact | undefined;

    if (!filterByContactId && contacts.current) {
      contact = contacts.current.contact;
    }

    if (filterByContactId && contacts.contacts) {
      contact = contacts.contacts.find(c => c._id === filterByContactId);
    }

    if (!contact || !contact._id) {
      return Promise.reject();
    }

    const params: BaseOmniRiskProfileParams = applyPersistenceKey({ contactId: contact._id }, user);

    return api.get(GET_OMNI_RISK_PROFILES, params).finally(() => {
      dispatch(stopLoaderAction(GET_OMNI_RISK_PROFILES.type));
    });
  };

export const loadOmniRiskProfileById =
  (omniRiskProfileId: FormattedFullOmniRiskProfile['_id']): Result<void> =>
  (dispatch, getState) => {
    const { omniRiskProfiles } = getState();

    if (!omniRiskProfiles?.riskProfiles) {
      return;
    }

    const omniRiskProfile = omniRiskProfiles.riskProfiles.find(riskProfile => riskProfile._id === omniRiskProfileId);

    if (!omniRiskProfile) {
      return;
    }

    dispatch({ ...LOAD_OMNI_RISK_PROFILE, omniRiskProfile });
  };

export const loadOmniRiskProfilesQuestionnaire =
  (formId: UUID): AsyncResult<OmniRiskProfilesQuestionnaire> =>
  dispatch =>
    api.post(LOAD_OMNI_RISK_PROFILES_QUESTIONNAIRE, {}, { formId }).then((res: { data: OmniRiskProfilesQuestionnaire }) => res.data);

export const refreshOmniRiskProfile =
  (riskProfile: FormattedFullOmniRiskProfile): AsyncResult<void> =>
  (dispatch, getState) => {
    const { contactId, _id: omniRiskProfileId, status } = riskProfile;
    const {
      auth: { user },
      contacts: { current },
    } = getState();

    // The refresh action should only be performed if the risk profile is approved
    if (!contactId || !omniRiskProfileId || status !== OmniRiskProfileStatus.Approved) {
      return Promise.reject();
    }

    if (current?.contact?._id !== contactId) {
      throw new Error('Current contact is not set or does not match the contactId provided');
    }

    const body = { contact: current.contact };
    const params: RefreshOmniRiskProfilesParams = applyPersistenceKey({ contactId, omniRiskProfileId }, user);

    return api.post(REFRESH_OMNI_RISK_PROFILE, params, body).then(({ data }: { data: Pick<FormattedFullOmniRiskProfile, '_id'> }) => {
      dispatch(loadOmniRiskProfileById(data._id));
    });
  };

export const removeEmail =
  (email: string, omniRiskProfile: FormattedFullOmniRiskProfile): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { contactId, _id: omniRiskProfileId } = omniRiskProfile;
    const {
      auth: { user },
    } = getState();

    if (!email || !contactId || !omniRiskProfileId) {
      return Promise.reject();
    }

    const params = applyPersistenceKey({ contactId, omniRiskProfileId }, user);
    return api.post(REMOVE_EMAIL, params, { removedEmails: [email] }).then(({ data }: { data: FormattedFullOmniRiskProfile }) => {
      dispatch(loadOmniRiskProfileById(data._id));
    });
  };

export const authorizeEmail =
  (email: string, omniRiskProfile: FormattedFullOmniRiskProfile, emailTemplate = OmniRiskProfileEmailTemplate.QuestionnaireInvitation): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { contactId, _id: omniRiskProfileId } = omniRiskProfile;
    const {
      auth: { user },
      contacts: { current },
    } = getState();

    if (!email || !contactId || !omniRiskProfileId) {
      return Promise.reject();
    }

    const params: AuthorizeEmailParams = applyPersistenceKey({ contactId, omniRiskProfileId }, user);

    const body = { emailTemplate, recipients: [email], contact: current?.contact };
    if (current?.contact?._id !== contactId) {
      throw new Error('Current contact is not set or does not match the contactId provided');
    }

    return api.post(AUTHORIZE_EMAIL, params, body).then(({ data }: { data: FormattedFullOmniRiskProfile }) => {
      dispatch(loadOmniRiskProfileById(data._id));
    });
  };

export const updateOmniRiskProfile =
  (profile: FormattedFullOmniRiskProfile, body: UpdateOmniRiskProfileBody): AsyncResult<FormattedFullOmniRiskProfile> =>
  async (dispatch, getState) => {
    const { _id: omniRiskProfileId, contactId } = profile;
    const {
      auth: { user },
    } = getState();

    const params: UpdateOmniRiskProfileParams = applyPersistenceKey({ contactId, omniRiskProfileId }, user);
    return api.patch(UPDATE_OMNI_RISK_PROFILE, params, body).then((res: { data: FormattedFullOmniRiskProfile }) => res.data);
  };
